import axios from 'axios';
import { environment } from '../environments/environment';

const apiEndpoint = environment.webservices.endpoint;

/**
 * Provider-side patient messaging.
 *
 * These are the CLINICIAN routes (`provider_messaging_blueprint`), not the
 * patient ones. The patient's identical-looking endpoints live under /portal and
 * wear `portal_patient_guard` — do not point this service at those: a clinician
 * holds no phr.patient_identity binding, so every call would 403.
 *
 * URLs carry the ref_id; `check_patient_status` resolves it server-side.
 *
 * NO QUOTA ENDPOINT EXISTS, deliberately. Membership tiers buy response TIME
 * only, and there is no allowance to read or display. `awaiting_count` below is
 * how many patients are waiting — an inbox badge, never a meter.
 */

export type MessageAuthorKind = 'patient' | 'provider';

export type AuthorRole = 'provider' | 'navigator' | 'care_team';

export interface ApiMessage {
  id: number;
  author_kind: MessageAuthorKind;
  /** The immutable signature (PORTAL_MESSAGING_ADR D14): who wrote this — name +
   *  coarse role. Null on a patient message (author_kind says "patient"). On a
   *  provider message these name the ACTUAL author, so a navigator relaying
   *  logistics is shown as the navigator, never masquerading as the cardiologist. */
  author_name: string | null;
  author_role: AuthorRole | null;
  body: string;
  created_on: number;
  read_on: number | null;
}

export interface ApiThread {
  id: number;
  /** Only a PROVIDER-opened thread sets one. Render `display_label`, not this. */
  subject: string | null;
  /** Always present and meaningful: subject → bound experiment name → first line
   *  of the opening message. The patient is never asked for a topic, so every
   *  patient-opened thread has a null subject — rendering it produced a list of
   *  rows all called "Patient message". */
  display_label: string;
  /** Latest message, trimmed — triage the list without opening each thread. */
  preview: string | null;
  preview_author: MessageAuthorKind | null;
  status: 'open' | 'closed';
  opened_by: MessageAuthorKind;
  experiment_id: number | null;
  last_message_on: number | null;
  created_on: number;
  /** Unix seconds the wait began — set by the FIRST unanswered patient message. */
  awaiting_reply_since: number | null;
  sla_due_on: number | null;
  sla_breached: boolean;
  unread_count: number;
  messages?: ApiMessage[];
}

export interface ThreadListResponse {
  threads: ApiThread[];
  /** Threads awaiting a reply. The tab badge. */
  awaiting_count: number;
  /** This patient's promised turnaround, in hours. */
  sla_hours: number;
}

/** A row in the CROSS-PATIENT worklist: a thread plus whose it is. The panel
 *  view needs the patient's name/ref to name the message and deep-link into
 *  that patient's Messages tab; the per-patient endpoints do not, because their
 *  patient is already in the URL. */
export interface ApiWorklistRow extends ApiThread {
  patient_ref_id: string | null;
  patient_label: string;
}

export interface AwaitingWorklistResponse {
  threads: ApiWorklistRow[];
  /** How many patients are waiting across the whole panel. An inbox count. */
  awaiting_count: number;
  /** How many of those are past their promised reply time. Highlight, not gate. */
  breached_count: number;
}

export class MessagingService {
  /** Threads for this patient, ordered by who has been waiting LONGEST. */
  async getThreads(refId: string): Promise<ThreadListResponse> {
    const res = await axios.get<{ data: ThreadListResponse }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/message-threads`,
    );
    return res.data.data;
  }

  async getThread(refId: string, threadId: number): Promise<ApiThread> {
    const res = await axios.get<{ data: ApiThread }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/message-threads/${threadId}`,
    );
    return res.data.data;
  }

  /** Reply. Stops the SLA clock and closes the worklist task.
   *  `close` marks the thread closed — a disposition, not a refusal: the patient
   *  reopens it simply by replying. */
  async reply(refId: string, threadId: number, body: string, close = false): Promise<ApiThread> {
    const res = await axios.post<{ data: ApiThread }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/message-threads/${threadId}/messages`,
      { body, close },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
    );
    return res.data.data;
  }

  /** Resolve a thread WITHOUT replying (PORTAL_MESSAGING_ADR D16). When the
   *  patient's last message needs no answer — a "thanks", a confirmation —
   *  clears the SLA clock and closes the thread, writing NO message. The patient
   *  reopens by replying, which starts a fresh clock. */
  async closeThread(refId: string, threadId: number): Promise<ApiThread> {
    const res = await axios.post<{ data: ApiThread }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/message-threads/${threadId}/close`,
      {},
    );
    return res.data.data;
  }

  /** Start a thread with the patient — e.g. delivering a readout. Starts no SLA
   *  clock: the physician is not waiting on himself. */
  async openThread(
    refId: string, body: string, subject?: string, experimentId?: number,
  ): Promise<ApiThread> {
    const res = await axios.post<{ data: ApiThread }>(
      `${apiEndpoint}/patients/${encodeURIComponent(refId)}/message-threads`,
      { body, subject, experiment_id: experimentId },
      { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } },
    );
    return res.data.data;
  }

  /** THE CROSS-PATIENT INBOX (PORTAL_MESSAGING_ADR D12/D13) — every patient's
   *  unanswered message across the whole panel, worst-first: breached first,
   *  then soonest-due. This is the surface that routes a message to a person;
   *  nothing did before. No ref_id — it is the panel, not one patient.
   *
   *  PHYSICIAN-ONLY server-side (`require_provider_role`): a navigator token
   *  403s. Breach here sorts and highlights; it never withholds care. */
  async getAwaitingWorklist(): Promise<AwaitingWorklistResponse> {
    const res = await axios.get<{ data: AwaitingWorklistResponse }>(
      `${apiEndpoint}/provider/message-threads/awaiting`,
    );
    return res.data.data;
  }
}

export const messagingService = new MessagingService();
